import sys from '@truckdown/jsclient';
import * as icons from '@truckdown/jsclient/icons';
import * as apis from '@truckdown/jsclient/apis';

import * as locations from '@truckdown/locations';
import { ILocationsUtilities } from '../Services/ILocationsUtilities';

import { defineComponent, ref, computed, onMounted, onUnmounted, nextTick, watch, inject } from 'vue';

const userInfo = computed(() => {
    return {
        isAuth: sys.framework.context.isAuthenticated(),
        isFleet: sys.framework.context.isInRole(apis.framework.SecurityConstants.Roles.FleetUserRole)
    };
});

sys.fontLibrary.add(icons.solid.faLocationDot);
sys.fontLibrary.add(icons.solid.faAngleDoubleRight);

export default defineComponent({
    props: {
        pageLimit: Number
    },
    setup: function (props, ctx) {
        var limit = props.pageLimit ?? 15;

        const isLoaded = ref<boolean>(false);
        const isLoading = ref<boolean>(false);

        const query_type = ref<string>('user');

        const total = ref<number>(0);
        const history = ref<locations.SearchRequestModel[]>([]);

        const services = ref<apis.tools.Service[]>([]);
        const loadServices = async function () {
            let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
            await utilities.loadServices;
            services.value = utilities.services;
        }

        const displayText = computed(() => {
            if (isLoaded.value && total.value > 0) {
                let plural = 's';
                if (history.value.length == 1) {
                    plural = '';
                }

                return 'Displaying ' + history.value.length + ' result' + plural + ' out of ' + total.value;
            }
            return '';
        });

        const showMore = computed(() => {
            if (!isLoaded.value) {
                return false;
            }

            if (!userInfo.value.isFleet) {
                return false;
            }

            return total.value > history.value.length;
        });

        const getMoreModel = function (): locations.GetSearchRequestsModel {
            if (history.value.length == 0) {
                return {};
            }

            let lastRec: locations.SearchRequestModel = history.value[history.value.length - 1];

            let dt: Date | undefined = undefined;
            if (lastRec?.time) {
                dt = new Date(lastRec.time);
            }

            return {
                showAll: (query_type.value == 'account'),
                limit: limit,
                lastId: lastRec?.id,
                lastTime: dt
            };
        }

        const loadMore = async function () {
            if (!isLoading.value) {
                isLoading.value = true;

                try {
                    let service = sys.framework.getRequiredService<locations.ISearchHistoryService>('ISearchHistoryService');
                    let model = getMoreModel();
                    var results = await service.getRequestHistory(model);
                    total.value = results.count;
                    history.value = history.value.concat(results.results);
                    sys.events.emit('set-search-history-count', results.count);
                } catch (err) {
                    console.log(err);
                    total.value = 0;
                    history.value = [];
                } finally {
                    isLoading.value = false;
                }
            }
        }

        const getSearchUrl = function (search: locations.SearchRequestModel) {
            let utilities = sys.framework.getRequiredService<ILocationsUtilities>('ILocationsUtilities');
            return utilities.generateUrl(search);
        }

        const runSearch = function (search: locations.SearchRequestModel) {
            var url = getSearchUrl(search);
            window.location.href = url;
        }

        const getLocation = function (search: locations.SearchRequestModel) {
            let cityDisplay = search.meta['city'] + ', ' + search.meta['regionCode'];

            var cityId = search.meta['cityId'];
            if (cityId && (cityId as string).length > 0) {
                return cityDisplay;
            }

            return search.meta['locationDisplay'] ?? cityDisplay;
        }

        const getServices = function (search: locations.SearchRequestModel) {
            let selected = services.value.filter((svs) => {
                return search.services.some((cd) => {
                    return cd == svs.code;
                });
            });

            return selected.map((svs) => {
                return svs.name;
            }).join(', ');
        }

        const getDetails = function (search: locations.SearchRequestModel) {
            return 'Search ran by ' + search.userDisplay + ' on ' + new Date(search.time).toLocaleDateString("en-US", {
                weekday: 'short',
                month: 'short',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }

        const loadHistory = async function () {
            if (!isLoading.value) {
                isLoading.value = true;

                try {
                    let service = sys.framework.getRequiredService<locations.ISearchHistoryService>('ISearchHistoryService');
                    let model: locations.GetSearchRequestsModel = {
                        showAll: (query_type.value == 'account'),
                        limit: limit
                    }
                    var results = await service.getRequestHistory(model);
                    total.value = results.count;
                    history.value = results.results;

                    sys.events.emit('set-search-history-count', results.count);
                } catch (err) {
                    console.log(err);
                    total.value = 0;
                    history.value = [];
                } finally {
                    isLoading.value = false;
                }
            }
        }

        const getUrl = function (path: string) {
            let currentUrl = window.location.pathname + window.location.search + window.location.hash;
            return path + '?returnUrl=' + encodeURIComponent(currentUrl);
        }

        onMounted(async () => {
            await loadServices();
            await loadHistory();
            isLoaded.value = true;
        });

        watch(query_type, async function (newVal: string, oldVal: string) {
            if (isLoading.value) {
                query_type.value = oldVal;
            } else {
                await loadHistory();
            }
        });

        return {
            userInfo,
            isLoaded,
            isLoading,
            total,
            history,
            displayText,
            getSearchUrl,
            getLocation,
            getServices,
            getDetails,
            runSearch,
            loadMore,
            showMore,
            query_type,
            getUrl
        };
    }
});
